/**
 * Pay by job type (Lj, Oct 11: "the task and pay percentage, based on project type"). How a person earns on a task
 * and what an hour on the job should earn both follow the PROJECT's job type, not one flat rule. Pure — no I/O.
 *
 *   type      the Earned line (sharepay.js, books shares.ts)                 Earned per hour target (fieldgoals.js)
 *   fixed     task share: labour × hours-weighted role share %              the bid's labour $ ÷ its hours, else contract × labour % ÷ estimated hours
 *   product   the same as fixed                                              the same as fixed
 *   hourly    no share: your hours × YOUR pay rate; bills hours × role rate  the project's bill rate, else the department's
 *   internal  none                                                           left out
 *   personal  none                                                           left out
 *
 * Project fields (all optional, SYNC.md §1): `labourPct` (the share of the contract that is labour, fixed jobs with
 * no bid; blank = the org default, else 40), `billRates` { roleId: $/h } (hourly jobs: this job's bill rate per role,
 * over the org's bill roles), `travelRate` ($/h, hourly jobs: drive time bills at this), `hourlyRate` (the job's one
 * bill rate, as before).
 */
import { contractValueOf, jobTypeOf } from './profit.js';
import { departmentOf } from './hierarchy.js';
import { isOverheadTask } from './deptscore.js';
import { canonTag } from './tags.js';
import { extraRateFor } from './scope.js';

const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const pos = (v) => (num(v) > 0 ? num(v) : null);
const r2 = (v) => Math.round(v * 100) / 100;

/** The labour % a fixed job with no bid uses when neither the project nor the org says (Lj: "say 40%"). */
export const DEFAULT_LABOUR_PCT = 40;
export const MAX_BILL_RATE = 1000;

/** How a job type pays: 'share' (fixed, product) · 'hourly' (T&M) · null (internal, personal, no type). */
export const JOB_PAY_MODE = Object.freeze({ fixed: 'share', product: 'share', hourly: 'hourly', internal: null, personal: null });
export const jobPayMode = (type) => JOB_PAY_MODE[type] ?? null;
/** Job types whose tasks pay by share. */
export const SHARE_JOB_TYPES = new Set(['fixed', 'product']);

/** The rule in one sentence, for Project settings' "How this job pays". */
export function jobPayRule(type) {
  switch (type) {
    case 'fixed': return 'Fixed price — each task’s labour is split between the people on it by hours × their role share %; the Earned line shows your share.';
    case 'product': return 'Product — paid like a fixed job: each task’s labour split by hours × role share %.';
    case 'hourly': return 'Hourly (T&M) — no shares: each person is paid their own rate for their hours, and the job bills every hour at the role’s bill rate.';
    case 'internal': return 'Internal — no earned line: hours here are cost, nothing is billed or shared.';
    case 'personal': return 'Personal — not paid work: no earned line, left out of Earned per hour.';
    default: return 'No job type yet — pick one under Job type to set how this job pays.';
  }
}

/** The labour % for a fixed job with no bid: the project's, else the org's (`policy.labourPct`), else 40. → { pct, from } */
export function labourPctOf(project, policy = null) {
  const own = num(project?.labourPct);
  if (own != null && own > 0 && own <= 100) return { pct: own, from: 'project' };
  const org = num(policy?.labourPct);
  if (org != null && org > 0 && org <= 100) return { pct: org, from: 'org' };
  return { pct: DEFAULT_LABOUR_PCT, from: 'default' };
}

/** A project's billRates as given → { roleId: $/h } (positive, ≤ $1,000, ≤ 20 roles) or null when none. */
export function cleanBillRates(v) {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const out = {};
  for (const [k, raw] of Object.entries(v).slice(0, 20)) {
    const id = String(k).trim().slice(0, 40);
    const n = num(raw);
    if (id && n != null && n > 0 && n <= MAX_BILL_RATE) out[id] = r2(n);
  }
  return Object.keys(out).length ? out : null;
}

/**
 * What an hour of this job bills, one number (the Field target on an hourly job): the project's bill rate, else the
 * mean of the project's own role rates, else the department's. → { rate, from: 'project' | 'department' | 'roles' | null }
 */
export function projectBillRate(project, dept = null) {
  if (pos(project?.hourlyRate)) return { rate: pos(project.hourlyRate), from: 'project' };
  const rates = Object.values(cleanBillRates(project?.billRates) || {});
  if (rates.length) return { rate: r2(rates.reduce((a, b) => a + b, 0) / rates.length), from: 'roles' };
  if (pos(dept?.hourlyRate)) return { rate: pos(dept.hourlyRate), from: 'department' };
  return { rate: null, from: null };
}

/**
 * The labour in a fixed / product job's price: the bid's labour budget and hours when the books job came from a bid
 * (opsJobs budget.labor + laborHoursBudget), else contract × labour % over the tasks' estimated hours.
 * `estMinutes` = the project's estimated minutes (leaf tasks, overhead left out). → { labour, hours, from, pct }
 */
export function labourInPrice(project, job = null, { estMinutes = 0, policy = null } = {}) {
  // The linked bid: labour at what it SOLD for (the books' own budget keeps labour at cost, which reads $40/h on an $88/h job).
  const sold = project?.bidBudget?.labour;
  if (pos(sold?.sell) && pos(sold?.hours)) return { labour: pos(sold.sell), hours: pos(sold.hours), from: 'bid', pct: null };
  const bidLabour = pos(job?.budget?.labor);
  const bidHours = pos(job?.laborHoursBudget);
  if (job?.estimateId && bidLabour && bidHours) return { labour: bidLabour, hours: bidHours, from: 'bid', pct: null };
  const contract = contractValueOf(project, job);
  const { pct } = labourPctOf(project, policy);
  if (!(contract > 0)) return { labour: null, hours: estMinutes > 0 ? estMinutes / 60 : null, from: null, pct };
  return { labour: r2((contract * pct) / 100), hours: estMinutes > 0 ? estMinutes / 60 : null, from: 'labourPct', pct };
}

/**
 * One job's Earned-per-hour target by its type.
 * → { rate, from: 'bid' | 'labourPct' | 'project' | 'department' | 'roles' | null, mode, why }  (rate null = no target)
 */
export function jobRateTarget(project, type, { job = null, dept = null, estMinutes = 0, policy = null } = {}) {
  const mode = jobPayMode(type);
  if (mode === 'share') {
    const L = labourInPrice(project, job, { estMinutes, policy });
    if (!(L.labour > 0)) return { rate: null, from: null, mode, why: 'no contract value or bid labour' };
    if (!(L.hours > 0)) return { rate: null, from: L.from, mode, why: 'no estimated hours' };
    return { rate: Math.round(L.labour / L.hours), from: L.from, mode, why: L.from === 'bid' ? 'the bid’s labour ÷ its hours' : `contract × ${L.pct}% labour ÷ estimated hours` };
  }
  if (mode === 'hourly') {
    const b = projectBillRate(project, dept);
    return { rate: b.rate, from: b.from, mode, why: b.rate ? (b.from === 'department' ? 'the department’s bill rate' : b.from === 'roles' ? 'the mean of this job’s role rates' : 'the project’s bill rate') : 'no bill rate' };
  }
  return { rate: null, from: null, mode: null, why: 'internal / personal — left out' };
}

/** "from the bid" / "40% labour" / "project rate" … — a job target's source in a few words. */
export function targetFromText(from, pct = null) {
  return from === 'bid' ? 'the bid' : from === 'labourPct' ? `${pct ?? DEFAULT_LABOUR_PCT}% labour` : from === 'department' ? 'the department’s rate'
    : from === 'roles' ? 'role rates' : from === 'project' ? 'project rate' : '';
}

/**
 * Project settings' "How this job pays" card. `roles` = the org's bill roles (opsRates/billRoles, admins; [] for
 * everyone else). → { type, mode, rule, labourPct: { pct, from } | null, bid: { labour, hours } | null,
 *   rates: [{ id, name, org, own, rate }] | null, travelRate, target: jobRateTarget }
 */
export function jobPayView(project, { type = null, job = null, dept = null, roles = [], policy = null, estMinutes = 0 } = {}) {
  const mode = jobPayMode(type);
  const own = cleanBillRates(project?.billRates) || {};
  const flat = pos(project?.hourlyRate);
  const L = mode === 'share' ? labourInPrice(project, job, { estMinutes, policy }) : null;
  const rates = mode === 'hourly'
    ? [...(roles || []).map((r) => ({ id: r.id, name: r.name || r.id, org: pos(r.billRate) })), ...Object.keys(own).filter((id) => !(roles || []).some((r) => r.id === id)).map((id) => ({ id, name: id, org: null }))]
      .map((r) => ({ ...r, own: own[r.id] ?? null, rate: own[r.id] ?? flat ?? r.org ?? pos(dept?.hourlyRate) }))
    : null;
  return {
    type, mode, rule: jobPayRule(type),
    labourPct: mode === 'share' ? labourPctOf(project, policy) : null,
    bid: L?.from === 'bid' ? { labour: L.labour, hours: L.hours } : null,
    rates, travelRate: mode === 'hourly' ? pos(project?.travelRate) : null,
    target: jobRateTarget(project, type, { job, dept, estMinutes, policy }),
  };
}

/** Check Project settings' pay fields → { patch, errors } (labourPct 1–100 or blank; travelRate; billRates). */
export function checkJobPayPatch(input = {}) {
  const patch = {}; const errors = {};
  const parse = (v) => (v == null || String(v).trim() === '' ? null : Number(String(v).replace(/[$,%\s]/g, '')));
  if ('extraRate' in input) {
    const n = parse(input.extraRate);
    if (n != null && !(Number.isFinite(n) && n > 0 && n <= MAX_BILL_RATE)) errors.extraRate = `A rate up to $${MAX_BILL_RATE}, or blank.`;
    else patch.extraRate = n;
  }
  if ('labourPct' in input) {
    const n = parse(input.labourPct);
    if (n != null && !(Number.isFinite(n) && n > 0 && n <= 100)) errors.labourPct = 'A % from 1 to 100, or blank for the default.';
    else patch.labourPct = n == null ? null : Math.round(n * 10) / 10;
  }
  if ('travelRate' in input) {
    const n = parse(input.travelRate);
    if (n != null && !(Number.isFinite(n) && n >= 0 && n <= MAX_BILL_RATE)) errors.travelRate = 'Numbers only, up to $1,000.';
    else patch.travelRate = n ? r2(n) : null;
  }
  if ('billRates' in input) {
    const raw = input.billRates || {};
    const bad = Object.entries(raw).find(([, v]) => { const n = parse(v); return n != null && !(Number.isFinite(n) && n >= 0 && n <= MAX_BILL_RATE); });
    if (bad) errors.billRates = 'Rates are numbers, up to $1,000.';
    else patch.billRates = cleanBillRates(Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, parse(v)])));
  }
  return { patch, errors };
}

/** A project's estimated minutes: its leaf tasks' estimates (an assembly counts through its parts), overhead left out. */
export function projectEstMinutes(tasks = [], projectId) {
  const mine = tasks.filter((t) => t && t.projectId === projectId && !t.deletedAt);
  const parents = new Set(mine.filter((t) => t.parentId && num(t.estimatedMinutes) > 0).map((t) => t.parentId));
  return mine.reduce((a, t) => (parents.has(t.id) || isOverheadTask(t) || !(num(t.estimatedMinutes) > 0) ? a : a + num(t.estimatedMinutes)), 0);
}

// ── Earned per hour: the ONE count (v0.1.261) ───────────────────────────────────────────────────────────────────────

const H = 3600000;
const dayOf = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const OVERHEAD_TAG = (t, tag) => Array.isArray(t?.tags) && t.tags.some((x) => canonTag(String(x)) === tag);
/** A drive on a job: a travel quick task or a #travel task (not setup / cleanup / a material run) — bills at the travel rate. */
export function isTravelTask(t) {
  if (!t || !isOverheadTask(t)) return false;
  if (t.bucket === 'setup' || t.bucket === 'cleanup' || t.quickTaskId === 'setup' || t.quickTaskId === 'cleanup') return false;
  if (t.quickTaskId === 'material-run' || OVERHEAD_TAG(t, 'materials')) return false;
  return t.quickTaskId === 'travel' || OVERHEAD_TAG(t, 'travel');
}

/**
 * Earned per hour, by JOB TYPE — the one count every Earned/h figure uses (the Field goal and dashboard, the daily /
 * 30-day department score, the day scrubber, the Daily Log's "$/h"). What the work finished in [from, to] earned ÷
 * every hour counted (overhead included — the hour on site has to pay):
 *   fixed / product  the LABOUR in the price (labourInPrice: bid labour $, else contract × labour %) × the finished
 *                    task's share of the job's whole estimate (leaf tasks, overhead out)
 *   hourly (T&M)     hours × the bill rate (projectBillRate: project rate, else role rates, else the department's);
 *                    travel tasks at the project's travel rate when it has one — never your pay rate
 *   internal / personal earn nothing (their hours still count) and have no target
 * A job with no type: a contract value makes it fixed, else T&M. Never the whole contract value.
 *
 * `entryIn(e, t)` picks the hours (a department's, a person's day); `taskIn(t)` picks the finished tasks. `tasks` must
 * be every task (a job's estimate is its whole estimate). `jobs` = the books' jobs (bid labour, contract); `policy` =
 * opsRates overhead policy (labourPct).
 * → { value ($/h, null = nothing priced or no hours), earned, ms, valued, fixedTasks, noEst, noLabour: [pid],
 *     tmNoRate: [{ pid, ms }], tmRated: [row], noneMs, byJob: Map(pid → { pid, fixed, tasks, tm, tmMs, rate, rateFrom, travelMs }),
 *     msByProject: Map, jobInfo(pid) → { p, job, d, contract, type, kind: 'fixed' | 'tm' | 'none', labour, target } }
 */
export function earnedByJobType({ tasks = [], projects = [], departments = [], jobs = [], policy = null, from, to, entryIn = () => true, taskIn = () => true } = {}) {
  const inWin = (d) => !!d && d >= from && d <= to;
  const projById = new Map(projects.map((p) => [p.id, p]));
  const jobById = new Map((jobs || []).map((j) => [j.id, j]));
  const parents = new Set(tasks.filter((t) => t.parentId && num(t.estimatedMinutes) > 0).map((t) => t.parentId));
  let ms = 0;
  const msByProject = new Map();
  const travelMsByProject = new Map();
  for (const t of tasks) {
    for (const e of t.timeEntries || []) {
      if (!inWin(e?.date) || !((e.duration || 0) > 0) || !entryIn(e, t)) continue;
      ms += e.duration;
      if (t.projectId) {
        msByProject.set(t.projectId, (msByProject.get(t.projectId) || 0) + e.duration);
        if (isTravelTask(t)) travelMsByProject.set(t.projectId, (travelMsByProject.get(t.projectId) || 0) + e.duration);
      }
    }
  }
  const estByProject = new Map();
  for (const t of tasks) {
    if (!t.projectId || isOverheadTask(t) || parents.has(t.id) || !(num(t.estimatedMinutes) > 0)) continue;
    estByProject.set(t.projectId, (estByProject.get(t.projectId) || 0) + num(t.estimatedMinutes));
  }
  const info = new Map();
  const jobInfo = (pid) => {
    if (!info.has(pid)) {
      const p = projById.get(pid) || null;
      const job = p?.bookProjectId ? jobById.get(p.bookProjectId) || null : null;
      const d = p ? departmentOf(p, departments) : null;
      const contract = p ? contractValueOf(p, job) : 0;
      const type = p ? jobTypeOf(p, job, d) : null;
      const mode = type ? jobPayMode(type) : contract > 0 ? 'share' : 'hourly';
      const kind = !p || !mode ? 'none' : mode === 'hourly' ? 'tm' : 'fixed';
      const estMinutes = estByProject.get(pid) || 0;
      const labour = kind === 'fixed' ? labourInPrice(p, job, { estMinutes, policy }) : null;
      const target = kind === 'none' ? null : jobRateTarget(p, kind === 'fixed' ? (type === 'product' ? 'product' : 'fixed') : 'hourly', { job, dept: d, estMinutes, policy });
      info.set(pid, { p, job, d, contract, type, kind, labour, target });
    }
    return info.get(pid);
  };
  const byJob = new Map();
  const row = (pid) => { if (!byJob.has(pid)) byJob.set(pid, { pid, fixed: 0, tasks: 0, tm: 0, tmMs: 0, rate: null, rateFrom: null, travelMs: 0 }); return byJob.get(pid); };
  // Fixed / product: a finished task's share of the labour is SPREAD over its time entries by duration (Lj,
  // 2026-10-26: "split it across the time entries for that task, that would be more accurate"), so a task worked
  // Tue–Thu and ticked Thu earns on all three days, not Thursday alone. A finished task with no time on it lands
  // whole on its completion day (the only day that knows about it). Only the hours this view counts (`entryIn`)
  // take their slice; the rest of the share belongs to whoever's hours they were.
  let earned = 0; let fixedTasks = 0; let noEst = 0; const noLabour = new Set();
  for (const t of tasks) {
    const done = t.status === 'done' && t.completedAt ? dayOf(t.completedAt) : null;
    if (!done || isOverheadTask(t) || !t.projectId || !taskIn(t) || t.scope === 'extra') continue;
    const j = jobInfo(t.projectId);
    if (j.kind !== 'fixed' || parents.has(t.id)) continue;   // a parent is counted through its subtasks
    const entries = (t.timeEntries || []).filter((e) => (e?.duration || 0) > 0 && !!e.date);
    const totalMs = entries.reduce((s, e) => s + e.duration, 0);
    const inHere = totalMs > 0
      ? entries.filter((e) => inWin(e.date) && entryIn(e, t)).reduce((s, e) => s + e.duration, 0) / totalMs
      : inWin(done) ? 1 : 0;
    if (!(inHere > 0)) continue;
    if (!(num(t.estimatedMinutes) > 0)) { noEst += 1; continue; }
    if (!(j.labour?.labour > 0)) { noLabour.add(t.projectId); continue; }
    const share = (j.labour.labour * num(t.estimatedMinutes)) / (estByProject.get(t.projectId) || num(t.estimatedMinutes)) * inHere;
    earned += share; fixedTasks += 1;
    const r = row(t.projectId); r.fixed += share; r.tasks += 1;
  }
  // An extra on a fixed job earns its hours at the extras rate, as they're worked: the client pays it on top.
  for (const t of tasks) {
    if (t.scope !== 'extra' || !t.projectId) continue;
    const j = jobInfo(t.projectId);
    const rate = j.kind === 'fixed' ? extraRateFor(j.p).rate : null;
    if (!rate) continue;
    const inHere = (t.timeEntries || []).filter((e) => (e?.duration || 0) > 0 && inWin(e.date) && entryIn(e, t)).reduce((s, e) => s + e.duration, 0);
    if (!inHere) continue;
    const pay = (inHere / H) * rate;
    earned += pay; fixedTasks += 1;
    const r = row(t.projectId); r.fixed += pay; r.tasks += 1;
  }
  const tmRated = []; const tmNoRate = []; let noneMs = 0;
  for (const [pid, pms] of msByProject) {
    const j = jobInfo(pid);
    if (j.kind === 'none') { noneMs += pms; continue; }
    if (j.kind !== 'tm') continue;
    const bill = projectBillRate(j.p, j.d);
    const rate = bill.rate > 0 ? bill.rate : null;
    if (!rate) { tmNoRate.push({ pid, ms: pms }); continue; }
    const travelRate = pos(j.p?.travelRate);
    const trMs = travelRate ? travelMsByProject.get(pid) || 0 : 0;
    const billed = ((pms - trMs) / H) * rate + (trMs / H) * (travelRate || 0);
    earned += billed;
    const r = row(pid); r.tm += billed; r.tmMs = pms; r.rate = rate; r.rateFrom = bill.from; r.travelMs = trMs;
    tmRated.push(r);
  }
  const valued = fixedTasks > 0 || tmRated.length > 0;
  return {
    value: valued && ms > 0 ? Math.round(earned / (ms / H)) : null, earned, ms, valued, fixedTasks, noEst, noLabour: [...noLabour], tmNoRate, tmRated, noneMs,
    byJob, msByProject, jobInfo,
  };
}
